import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ThemeProvider,
  ToastProvider,
  useToast,
  useTheme
} from './PrivateClassShared';
import './CourseVideos.css';

import {
  BooksEngineProvider,
  useTeachersForSubject,
  engine,
} from '../hooks/useBooksEngine.js';

import { normalizeGradeId } from '../core/education/curriculum.js';

// ==================== داده‌های نمونه معلمان ====================
const TEACHERS_DATA = {
  
};

// ==================== کامپوننت ستاره‌های نمایشی (ثابت) ====================
const StaticStars = ({ stars, size = 14 }) => {
  const full = Math.floor(stars);
  const half = stars % 1 >= 0.5 ? 1 : 0;
  const empty = 5 - full - half;
  return (
    <>
      {[...Array(full)].map((_, i) => (
        <span key={`full-${i}`} className="star-filled" style={{ fontSize: size }}>★</span>
      ))}
      {half === 1 && <span className="star-filled" style={{ fontSize: size }}>★</span>}
      {[...Array(empty)].map((_, i) => (
        <span key={`empty-${i}`} className="star-empty" style={{ fontSize: size }}>★</span>
      ))}
    </>
  );
};

// ==================== کامپوننت ستاره‌های قابل کلیک (امتیاز دانش‌آموز) ====================
const ClickableStars = ({ rating, onRate, size = 16 }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="student-stars">
      {[1, 2, 3, 4, 5].map(r => (
        <span
          key={r}
          className={`clickable-star ${(hover || rating) >= r ? 'active' : ''}`}
          onMouseEnter={() => setHover(r)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onRate && onRate(r)}
          style={{ fontSize: size }}
        >
          ★
        </span>
      ))}
    </div>
  );
};

// ==================== کامپوننت مودال ====================
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>
        {title && <h2 style={{ marginBottom: '1rem', color: 'var(--cv-text-primary)' }}>{title}</h2>}
        {children}
      </div>
    </div>
  );
};





// ==================== Wrapper برای BooksEngine ====================
function CourseVideosWithEngine() {
  const { user } = useAuth();
  const showToast = useToast();
  return (
    <BooksEngineProvider user={user} toast={showToast}>
      <CourseVideosContent />
    </BooksEngineProvider>
  );
}





// ==================== صفحه اصلی ====================
function CourseVideosContent() {
  const { grade: rawGrade, subject } = useParams();
const grade = normalizeGradeId(rawGrade) || rawGrade;
  const navigate = useNavigate();
  const { user } = useAuth();
  const showToast = useToast();
  const { theme, toggleTheme } = useTheme();

  // ===== بازگشت ایمن =====
  // اگه کاربر از داخل اپ اومده → برگرد به صفحه‌ی قبلی
  // اگه مستقیم وارد شده → برو به دوره (جلوگیری از صفحه‌ی سیاه)
  const handleBack = () => {
    if (window.history.length <= 1 || document.referrer === '') {
      navigate(`/course/${grade}/${subject}`, { replace: true });
    } else {
      navigate(-1);
    }
  };

  const [teachers, setTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState('popular');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [ratings, setRatings] = useState({});
  const [watched, setWatched] = useState({});
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [aggRatings, setAggRatings] = useState({});  // 🆕
  const searchRef = useRef(null);
const dynamicTeachers = useTeachersForSubject(grade, subject);

  // بارگذاری داده‌ها
  // بارگذاری داده‌ها (داینامیک از registry + fallback به legacy)
useEffect(() => {
  const key = `${grade}-${subject}`;
  const legacyTeachers = TEACHERS_DATA[key] || [];

  // ادغام: معلم‌های داینامیک اول، بعد legacy (بدون تکرار)
  const merged = [...(dynamicTeachers || [])];
  for (const lt of legacyTeachers) {
    const isDuplicate = merged.some(
      (mt) =>
        (lt.folderName && mt.folderName === lt.folderName) ||
        (mt.id && lt.id && mt.id === lt.id)
    );
    if (!isDuplicate) merged.push(lt);
  }

  // ست اولیه (بدون تصویر برای داینامیک‌ها)
  setTeachers(merged);
  setFilteredTeachers(merged);

  // بارگذاری rating و watched
  // ШЁШ§ШұЪҜШ°Ш§ШұЫҢ rating ЩҲ watched — per-user
  // بارگذاری rating و watched — از engine (هماهنگ با remoteSync)
  if (user?.id) {
    try {
      const savedRatings = engine.get(`teacher-ratings-${user.id}`, null);
      if (savedRatings) setRatings(savedRatings);
    } catch {}
    try {
      const savedWatched = engine.get(`watched-teachers-${user.id}`, null);
      if (savedWatched) setWatched(savedWatched);
    } catch {}
  }

  // حل کردن avatarBlobId → URL برای معلم‌های داینامیک
  let cancelled = false;
  (async () => {
    try {
      const resolved = await Promise.all(
        merged.map(async (t) => {
          if (t.avatarBlobId && !t.image) {
            try {
              const url = await engine.blobs.getUrl(t.avatarBlobId);
              return { ...t, image: url || null };
            } catch {
              return t;
            }
          }
          return t;
        })
      );
      if (!cancelled) {
        setTeachers(resolved);
        setFilteredTeachers(resolved);
      }
    } catch (e) {
      // بی‌صدا رد شو — legacy همچنان کار می‌کنه
    }
  })();

  return () => { cancelled = true; };
}, [grade, subject, dynamicTeachers]);

  // ذخیره‌سازی
  // Ш°Ш®ЫҢШұЩҮвҖҢШіШ§ШІЫҢ — per-user
  // ذخیره‌سازی
  useEffect(() => {
    if (!user?.id) return;
    try {
      engine.set(`teacher-ratings-${user.id}`, ratings);
    } catch {}
  }, [ratings, user?.id]);

  // 🆕 تجمیع امتیازهای واقعی معلم‌ها از سرور
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const byTeacher = {};

      try {
        const res = await fetch(`${API_BASE}/teacherRatings`);
        if (res.ok) {
          const list = await res.json();
          if (Array.isArray(list)) {
            for (const rec of list) {
              const data = rec.data || {};
              for (const key in data) {
                const r = Number(data[key]);
                if (r <= 0) continue;
                // key = `${grade}-${subject}-${teacherId}`
                const parts = key.split('-');
                if (parts.length < 3) continue;
                const tId = parts[parts.length - 1];
                if (!byTeacher[tId]) byTeacher[tId] = [];
                byTeacher[tId].push(r);
              }
            }
          }
        }
      } catch (e) {
        console.warn('teacherRatings fetch failed:', e);
      }

      if (cancelled) return;

      const result = {};
      for (const tId in byTeacher) {
        const arr = byTeacher[tId];
        const sum = arr.reduce((s, r) => s + r, 0);
        result[tId] = {
          stars: Math.round((sum / arr.length) * 10) / 10,
          studentsRated: arr.length,
          _sum: sum,
          _count: arr.length,
        };
      }
      setAggRatings(result);
    };

    load();
    const onFocus = () => load();
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [grade, subject]);
  useEffect(() => {
    if (!user?.id) return;
    try {
      // 🆕 از engine → remoteSync → watchedTeachers (سرور)
      engine.set(`watched-teachers-${user.id}`, watched);
    } catch {}
  }, [watched, user?.id]);

  // فیلتر و جستجو
  // 🆕 ادغام امتیاز واقعی در آرایه‌ی معلمان
  const teachersWithRealRatings = useMemo(() => {
    return teachers.map(t => {
      const agg = aggRatings[t.id];
      return {
        ...t,
        _displayStars: agg?.stars ?? t.stars ?? 0,
        _displayStudentsRated: agg?.studentsRated ?? t.studentsRated ?? 0,
      };
    });
  }, [teachers, aggRatings]);

  // فیلتر و جستجو
  useEffect(() => {
    let result = [...teachersWithRealRatings];
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(t => t.name.toLowerCase().includes(q));
    }
    if (filterMode === 'special') {
      result.sort((a, b) => (b.isSpecial ? 1 : 0) - (a.isSpecial ? 1 : 0));
    } else {
      result.sort((a, b) => b._displayStars - a._displayStars);
    }
    setFilteredTeachers(result);
  }, [teachersWithRealRatings, searchQuery, filterMode]);

  // اسکرول هدر
  useEffect(() => {
    const handleScroll = () => {
      setHeaderScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // بررسی دیده شدن فیلم
  const hasWatched = (teacherId) => {
    if (!user) return false;
    return watched[`${grade}-${subject}-${teacherId}`] === true;
  };

  // امتیازدهی دانش‌آموز
  const handleRate = (teacherId, rating) => {
    if (!user) {
      showToast('لطفاً ابتدا وارد شوید.', 'error');
      return;
    }
    if (!hasWatched(teacherId)) {
      showToast('شما هنوز فیلم‌های این معلم را ندیده‌اید.', 'warning');
      return;
    }

    const key = `${grade}-${subject}-${teacherId}`;
    const oldRating = ratings[key] || 0;

    setRatings(prev => ({ ...prev, [key]: rating }));

    // 🆕 optimistic update روی تجمیع واقعی
    setAggRatings(prev => {
      const cur = prev[teacherId] || {
        stars: 0, studentsRated: 0, _sum: 0, _count: 0,
      };
      const prevSum = Number(cur._sum) || (Number(cur.stars) * Number(cur.studentsRated)) || 0;
      const prevCount = Number(cur._count) || Number(cur.studentsRated) || 0;

      let newSum, newCount;
      if (oldRating > 0) {
        newSum = prevSum - oldRating + rating;
        newCount = prevCount;
      } else {
        newSum = prevSum + rating;
        newCount = prevCount + 1;
      }

      return {
        ...prev,
        [teacherId]: {
          stars: newCount > 0 ? Math.round((newSum / newCount) * 10) / 10 : 0,
          studentsRated: newCount,
          _sum: newSum,
          _count: newCount,
        },
      };
    });

    showToast(`امتیاز ${rating} ستاره برای ${teachers.find(t => t.id === teacherId)?.name} ثبت شد`, 'success');
  };

  const openModal = (teacher) => {
    const agg = aggRatings[teacher.id];
    setSelectedTeacher({
      ...teacher,
      _displayStars: agg?.stars ?? teacher.stars ?? 0,
      _displayStudentsRated: agg?.studentsRated ?? teacher.studentsRated ?? 0,
    });
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setSelectedTeacher(null);
  };

  return (
    <div className={`course-videos-page ${theme === 'dark' ? 'dark-mode' : ''}`}>
      {/* پس‌زمینه ذرات */}
      <div className="bg-particles">
        {[...Array(12)].map((_, i) => (
          <span
            key={i}
            className="bg-particle"
            style={{
              '--i': i,
              '--size': (5 + (i % 8)) + 'px',
              '--x': (i * 7) % 100 + '%',
              '--y': (i * 11) % 100 + '%'
            }}
          />
        ))}
      </div>

      {/* هدر - شاد با المان‌ها در وسط */}
      <header className={`cv-header ${headerScrolled ? 'scrolled' : ''}`}>
        <div className="header-glow-ring ring-1"></div>
        <div className="header-glow-ring ring-2"></div>

        {/* دکمه بازگشت - گوشه راست */}
        <div className="back-btn-wrapper">
          <button className="back-btn" onClick={handleBack}>
            <i className="fas fa-arrow-right"></i>
          </button>
        </div>

        {/* المان‌های وسط */}
        <div className="header-center">
          <div className="header-title">
            <h1>
              <span className="highlight">استاد</span>‌های <span className="highlight">برتر</span>
            </h1>
          </div>
          <span className="header-sub">✨ بهترین معلم‌ها را پیدا کنید</span>

          <div className="header-tools">
            {/* فیلترهای آیکونی دوحالته */}
            <div className="filter-toggle-group">
              <button
                className={`filter-toggle-btn ${filterMode === 'popular' ? 'active' : ''}`}
                onClick={() => setFilterMode('popular')}
              >
                <i className="fas fa-star"></i>
                <span className="label">پرستاره</span>
              </button>
              <button
                className={`filter-toggle-btn ${filterMode === 'special' ? 'active' : ''}`}
                onClick={() => setFilterMode('special')}
              >
                <i className="fas fa-crown"></i>
                <span className="label">ویژه</span>
              </button>
            </div>

            {/* جستجو */}
            <div className="search-box-wrapper" ref={searchRef}>
              <div className="search-box">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="جستجو..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="search-clear" onClick={() => setSearchQuery('')}>
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        
      </header>

      {/* 🆕 بنر مخصوص معلم — دعوت به ساخت کلاس اختصاصی */}
      {user?.role === 'teacher' && (
        <div className="teacher-build-banner">
          <div className="teacher-build-banner-inner">
            <div className="teacher-build-banner-content">
              <span className="teacher-build-banner-icon">🎓</span>
              <div className="teacher-build-banner-text">
                <h3>شما یک معلم هستید!</h3>
                <p>
                  می‌تونید کلاس اختصاصی خودتون رو بسازید و محتوای آموزشی‌تون رو با دانش‌آموزان به اشتراک بگذارید
                </p>
              </div>
            </div>
            <button
              type="button"
              className="teacher-build-banner-btn"
              onClick={() => navigate('/teacher/books')}
            >
              <i className="fas fa-plus-circle" />
              درخواست ساخت کلاس
            </button>
          </div>
        </div>
      )}

      {/* محتوای اصلی */}
      <div className="cv-content">
        <div className="teachers-grid">
          {filteredTeachers.length === 0 ? (
            <div className="empty-state">
              <span>🔍</span>
              <p>هیچ معلمی یافت نشد</p>
            </div>
          ) : (
            filteredTeachers.map((teacher, index) => {
              const isWatched = hasWatched(teacher.id);
              const userRating = ratings[`${grade}-${subject}-${teacher.id}`] || 0;

              return (
                <div
                  key={teacher.id}
                  className="teacher-card glass"
                  style={{ animation: `cv-bounce-in 0.5s ease ${index * 0.06}s forwards`, opacity: 0 }}
                  onClick={() => openModal(teacher)}
                >
                  {teacher.isSpecial && (
                    <div className="badge special">
                      <i className="fas fa-crown"></i> ویژه
                    </div>
                  )}
                  {isWatched && (
                    <div className="badge watched">
                      <i className="fas fa-eye"></i> دیده‌شده
                    </div>
                  )}

                  <div
                    className="teacher-img"
                    style={{ backgroundImage: `url(${teacher.image})` }}
                  >
                    <div className="overlay">
                      <div className="info">
                        <h3>{teacher.name}</h3>
<div className="stars-row">
  <StaticStars stars={teacher._displayStars} size={14} />
  <span className="star-count">
    ({teacher._displayStudentsRated.toLocaleString('fa-IR')})
  </span>
</div>
                        <div className="desc">{teacher.description}</div>
                        <button
                          className="more-btn"
                          onClick={(e) => { e.stopPropagation(); openModal(teacher); }}
                        >
                          <i className="fas fa-chevron-down"></i> بیشتر
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* بخش امتیازدهی دانش‌آموز */}
                  {isWatched && (
                    <div className="rating-section">
                      <span className="rating-label">امتیاز شما:</span>
                      <ClickableStars
                        rating={userRating}
                        onRate={(r) => handleRate(teacher.id, r)}
                        size={16}
                      />
                      {userRating > 0 && (
                        <span className="rating-confirm">
                          <i className="fas fa-check-circle"></i> ثبت شد
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* مودال */}
      <Modal isOpen={showModal} onClose={closeModal} title={selectedTeacher?.name}>
        {selectedTeacher && (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <img
                src={selectedTeacher.image}
                alt={selectedTeacher.name}
                style={{ width: '180px', height: '180px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #ffffff' }}
              />
            </div>
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
<div className="stars-row" style={{ justifyContent: 'center', display: 'flex' }}>
  <StaticStars stars={selectedTeacher._displayStars} size={18} />
  <span style={{ marginRight: '0.5rem', fontSize: '0.8rem', color: 'var(--cv-text-muted)' }}>
    ({selectedTeacher._displayStudentsRated.toLocaleString('fa-IR')} نفر)
  </span>
</div>
            </div>
            <p style={{ lineHeight: 1.8, color: 'var(--cv-text-secondary)' }}>
              {selectedTeacher.fullDescription}
            </p>
            {selectedTeacher.isSpecial && (
              <div style={{ margin: '1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgb(255, 211, 53)', padding: '0.3rem 1rem', borderRadius: '30px', border: '1px solid rgba(212,175,55,0.1)' }}>
                <i className="fas fa-crown" style={{ color: '#ffffff' }}></i>
                <span style={{ fontWeight: 600, color: '#ffffff' }}>استاد ویژه</span>
              </div>
            )}
            <button
  className="btn-primary gradient-animate"
  style={{ width: '100%', marginTop: '1.5rem' }}
  onClick={() => {
    const key = `${grade}-${subject}-${selectedTeacher.id}`;
    setWatched(prev => ({ ...prev, [key]: true }));
    // ✅ ارسال کل شیء معلم
    navigate(`/book/${grade}/${subject}`, {
      state: { teacher: selectedTeacher }
    });
    closeModal();
  }}
>
  <i className="fas fa-play"></i> شروع تماشا
</button>
          </>
        )}
      </Modal>
    </div>
  );
}

// ==================== خروجی ====================
// ==================== خروجی ====================
export default function CourseVideos() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <CourseVideosWithEngine />
      </ToastProvider>
    </ThemeProvider>
  );
}